import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { observer } from 'mobx-react'

import { Alert } from 'Components/_theme'
import type { CardDetailsListItem } from 'Components/CardDetailsList/CardDetailsList.types'
import { FormatDate } from 'Components/FormatDate/FormatDate'
import { FormattedCurrency } from 'Components/FormattedCurrency/FormattedCurrency'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'
import { PresentationCard } from 'Components/PresentationCard/PresentationCard'
import { ScrollToTop } from 'Components/ScrollToTop/ScrollToTop'

import { useStore } from 'Stores/index'

import { useIsOrganisation } from 'Hooks'

import type { DeviceTypeEnum } from 'Types/deviceTypes'

import { GLOBAL_ROUTE_PATHS } from 'Portal/src/router/Router.constants'

import { OrderStickyFooter } from '../../OrderStickyFooter/OrderStickyFooter'
import { OrderUpgradeFee } from '../../OrderUpgradeFee/OrderUpgradeFee'
import type { DeviceRentalStepProps } from '../Steps.types'

export const DeviceRentalStep = observer((props: DeviceRentalStepProps) => {
  const { handleNextStep, isEarlyUpgrade } = props
  const { rentalStore } = useStore()
  const { rentalDetails, isLoadingRentalDetails, isRentalDetailsError } =
    rentalStore
  const isOrganisation = useIsOrganisation()
  const { origin, pathname } = window.location

  useEffect(() => {
    if (
      isEarlyUpgrade &&
      !rentalDetails?.rental.actions.showEarlyUpgradePenaltyAmount
    ) {
      return
    }

    if (
      rentalDetails?.rental.actions.showDeviceProlongingAction &&
      handleNextStep
    ) {
      handleNextStep()
    }
  }, [
    handleNextStep,
    isEarlyUpgrade,
    rentalDetails?.rental.actions.showDeviceProlongingAction,
    rentalDetails?.rental.actions.showEarlyUpgradePenaltyAmount,
  ])

  if (isLoadingRentalDetails) {
    return <PageLoadingState />
  }

  if (isRentalDetailsError || !rentalDetails) {
    return <PageErrorState />
  }

  const { rental } = rentalDetails

  const generateListItems: CardDetailsListItem[] = [
    ...(isOrganisation && rental.amount
      ? [
          {
            label: 'RentalDetails_monthly_amount',
            value: <FormattedCurrency value={rental.amount} />,
          },
        ]
      : []),
    {
      label: 'RentalDetails_copayment',
      value: <FormattedCurrency value={rental.coPaymentAmount} />,
    },
    {
      label: 'RentalDetails_minimum_term',
      value: (
        <FormattedMessage
          id="RentalDetails_months"
          values={{
            total: rental.minimumContractDuration,
          }}
        />
      ),
    },
    {
      label: 'RentalDetails_start_of_rental',
      value: <FormatDate value={rental.startAt} />,
    },
    ...(rental.rentalProlonging
      ? [
          {
            label: 'Prolongation_valid_till',
            value: <FormatDate value={rental.rentalProlonging?.validTill} />,
          },
        ]
      : [
          {
            label: 'RentalDetails_select_a_new',
            value: <FormatDate value={rental.newDeviceDate} />,
          },
        ]),
  ]

  const contactPath = `${origin}${pathname}#${GLOBAL_ROUTE_PATHS.public.contact}`

  return (
    <>
      <div className="py-6">
        <ScrollToTop />

        {isEarlyUpgrade ? (
          <p className="mb-6">
            <FormattedMessage id="Order_EarlyUpgrade_Info" />
          </p>
        ) : (
          <p className="mb-6">
            <FormattedMessage id="Order_Upgrade_Info" />
          </p>
        )}

        <PresentationCard
          cardClassName="DeviceRentalStep"
          header={rental.deviceDefinition?.name ?? ''}
          imagePlaceholderType={
            rental.deviceDefinition?.productType as DeviceTypeEnum
          }
          imageSource={rental.deviceDefinition?.imageURL}
          listItems={generateListItems}
        />

        {isEarlyUpgrade &&
          rentalDetails?.rental.actions.showEarlyUpgradePenaltyAmount && (
            <OrderUpgradeFee
              className="mt-6"
              price={rental.upgradeAmount || 0}
              showCopaymentInformation={rental.coPaymentAmount > 0}
            />
          )}

        {isEarlyUpgrade &&
          !rentalDetails?.rental.actions.showEarlyUpgradePenaltyAmount && (
            <Alert
              buttonRight={{
                asExternalLink: true,
                children: <FormattedMessage id="Contact_us" />,
                id: 'contact-us',
                target: '_blank',
                to: contactPath,
              }}
              className="mt-6"
              iconLeft={faInfoCircle}
              variant="info"
            >
              <FormattedMessage id="Order_EarlyUpgrade_Upgrade_Fee_Contact" />
            </Alert>
          )}
      </div>

      <OrderStickyFooter
        handleNextAction={handleNextStep}
        hideNextButton={
          isEarlyUpgrade &&
          !rentalDetails?.rental.actions.showEarlyUpgradePenaltyAmount
        }
      />
    </>
  )
})
